import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarCheck,
  CalendarCog,
  CalendarDays,
  CalendarX,
  Search,
  UserRound,
} from "lucide-react";
import { Eyebrow, Reveal } from "../components/Reveal";
import { DocCareMockup } from "../components/DocCareMockup";
import Footer from "../components/Footer";

const META = [
  { label: "Domain", value: "Healthcare" },
  { label: "Engagement", value: "Product concept / case study" },
  { label: "Role", value: "Business Analyst | Product Consultant" },
];

const DELIVERABLES = [
  "Business Requirements",
  "Product Requirements",
  "User Stories",
  "Business Rules",
  "Product Backlog",
  "UAT Scenarios",
  "Working Application Demo",
];

const CAPABILITIES = [
  { icon: Search, label: "Doctor Search" },
  { icon: UserRound, label: "Doctor Profiles" },
  { icon: CalendarDays, label: "Availability" },
  { icon: CalendarCheck, label: "Appointment Booking" },
  { icon: CalendarCog, label: "Appointment Management" },
  { icon: CalendarX, label: "Reschedule / Cancel" },
  { icon: Bell, label: "Notifications" },
];

const APPROACH = [
  {
    n: "01",
    title: "Discover",
    desc: "Framed the core problem: patients need a simple way to find a doctor and book an appointment without phone calls or front-desk queues.",
  },
  {
    n: "02",
    title: "Analyse",
    desc: "Mapped the appointment workflow end to end — search, availability, booking, rescheduling, cancellation and notifications — along with the business rules behind each step.",
  },
  {
    n: "03",
    title: "Define",
    desc: "Documented the product scope, user journeys and product requirements (PRD) for patients and doctors.",
  },
  {
    n: "04",
    title: "Structure",
    desc: "Broke the requirements into user stories with acceptance criteria and a prioritised product backlog.",
  },
  {
    n: "05",
    title: "Build",
    desc: "Developed the working application demo directly from the structured requirements and backlog.",
  },
  {
    n: "06",
    title: "Validate",
    desc: "Prepared UAT scenarios and validated the delivered flows against the documented requirements.",
  },
];

const BUSINESS_RULES = [
  "A patient cannot hold two confirmed appointments with the same doctor at overlapping times.",
  "A time slot becomes unavailable to other patients the moment a booking is confirmed.",
  "Rescheduling releases the original slot immediately and is subject to the doctor's current availability.",
  "Cancellations trigger a notification and return the slot to the doctor's available pool.",
];

const SAMPLE_STORY = {
  title: "Sample user story",
  story:
    "As a patient, I want to see a doctor's available time slots for a chosen date, so that I can book an appointment at a time that suits me.",
  criteria: [
    "Given a doctor profile, when I select a date, then I see all available slots for that date.",
    "Slots that are already booked are shown as unavailable and cannot be selected.",
    "On confirming a slot, I receive a booking confirmation with the doctor, date and time.",
  ],
};

export default function DocCarePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream text-navy">
      {/* Minimal header */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-navy/10 bg-cream/90 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
          <Link
            to="/"
            data-testid="doccare-nav-logo"
            className="flex items-center gap-2.5 font-display text-xl font-semibold tracking-tight"
          >
            <span className="block h-2.5 w-2.5 bg-rust" aria-hidden="true" />
            Kaizen&nbsp;Labs
          </Link>
          <Link
            to="/"
            data-testid="doccare-back-home-link"
            className="group inline-flex items-center gap-2 rounded-full border border-navy/25 px-5 py-2.5 text-sm font-medium transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-cream"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Back to Home
          </Link>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section data-testid="doccare-hero" className="border-b border-navy/10 bg-navy text-cream">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-20 pt-36 md:grid-cols-12 md:px-12 md:pb-28 md:pt-44">
            <div className="md:col-span-6">
              <Reveal>
                <Eyebrow dark>Healthcare Product Case Study</Eyebrow>
                <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
                  DocCare — healthcare appointments, made{" "}
                  <em className="text-rust">simple</em>.
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-cream/70 md:text-lg">
                  A healthcare appointment-booking product concept designed
                  from business requirements through product definition and a
                  working application.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <dl className="mt-10 space-y-4 border-t border-cream/15 pt-8">
                  {META.map((m) => (
                    <div key={m.label} className="flex flex-wrap items-baseline gap-x-6">
                      <dt className="w-28 shrink-0 text-xs font-medium uppercase tracking-[0.2em] text-cream/45">
                        {m.label}
                      </dt>
                      <dd className="text-sm text-cream/85">{m.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-8 border-l-2 border-rust pl-4 text-xs italic leading-relaxed text-cream/50">
                  DocCare is a portfolio product concept — designed and
                  documented by Kaizen Labs as a case study. It was not built
                  for a client.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-6">
              <Reveal delay={0.2}>
                <div className="relative">
                  <div
                    className="absolute -left-4 -top-4 h-full w-full rounded-xl border border-cream/10"
                    aria-hidden="true"
                  />
                  <DocCareMockup />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Problem framing */}
        <section data-testid="doccare-problem" className="py-24 md:py-32">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
            <div className="md:col-span-5">
              <Reveal>
                <Eyebrow>The Problem</Eyebrow>
                <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
                  Booking a doctor should not require a phone call.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-7">
              <Reveal delay={0.1}>
                <div className="space-y-6 text-base leading-relaxed text-navy/75 md:text-lg">
                  <p>
                    Appointment booking is a workflow every patient recognises:
                    find the right doctor, check availability, book a slot,
                    and manage changes when plans shift. Done over phone calls
                    and registers, it creates friction for patients and
                    administrative load for clinics.
                  </p>
                  <p>
                    This concept explores how that everyday workflow can be
                    turned into a structured, development-ready digital
                    product — starting not with screens, but with business
                    requirements, rules and user journeys.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Approach */}
        <section data-testid="doccare-approach" className="border-y border-navy/10 bg-white py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <Eyebrow>Approach</Eyebrow>
              <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
                From idea to working demo
              </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 gap-px border border-navy/15 bg-navy/15 sm:grid-cols-2 lg:grid-cols-3">
              {APPROACH.map((step, i) => (
                <Reveal key={step.n} delay={(i % 3) * 0.07}>
                  <div
                    data-testid={`doccare-approach-step-${i}`}
                    className="h-full bg-white p-8"
                  >
                    <p className="font-display text-3xl text-rust/80">{step.n}</p>
                    <h3 className="mt-4 text-base font-semibold tracking-tight">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy/65">
                      {step.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Deliverables + capabilities */}
        <section data-testid="doccare-deliverables" className="py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="grid grid-cols-1 gap-14 md:grid-cols-2">
              <Reveal>
                <div>
                  <Eyebrow>Deliverables</Eyebrow>
                  <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl">
                    What the engagement produced
                  </h2>
                  <ul className="mt-8">
                    {DELIVERABLES.map((d, i) => (
                      <li
                        key={d}
                        data-testid={`doccare-deliverable-${i}`}
                        className="flex items-center gap-4 border-b border-navy/10 py-4"
                      >
                        <span className="font-display text-sm text-rust">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-base font-medium tracking-tight">{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div>
                  <Eyebrow>Key Capabilities</Eyebrow>
                  <h2 className="mt-5 font-display text-3xl font-semibold leading-[1.15] tracking-tight md:text-4xl">
                    What the product does
                  </h2>
                  <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {CAPABILITIES.map((c, i) => (
                      <div
                        key={c.label}
                        data-testid={`doccare-capability-${i}`}
                        className={`flex items-center gap-3 border border-navy/15 bg-white px-5 py-4 ${
                          i === CAPABILITIES.length - 1 ? "sm:col-span-2" : ""
                        }`}
                      >
                        <c.icon className="h-4 w-4 shrink-0 text-rust" strokeWidth={1.5} />
                        <span className="text-sm font-medium">{c.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Documentation excerpts */}
        <section data-testid="doccare-excerpts" className="border-y border-navy/10 bg-sage/30 py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <Eyebrow>Documentation Excerpts</Eyebrow>
              <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
                A look at the actual artifacts
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-navy/70">
                Examples from the concept's own documentation — the kind of
                development-ready material every Kaizen Labs engagement is
                structured around.
              </p>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Reveal>
                <div className="h-full border border-navy/15 bg-white p-8">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-rust">
                    Business rules — excerpt
                  </p>
                  <ul className="mt-6 space-y-4">
                    {BUSINESS_RULES.map((rule, i) => (
                      <li key={i} className="flex gap-4 text-sm leading-relaxed text-navy/80">
                        <span className="shrink-0 font-display text-sm text-navy/40">
                          BR-{String(i + 1).padStart(2, "0")}
                        </span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="h-full border border-navy/15 bg-navy p-8 text-cream">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-sage">
                    {SAMPLE_STORY.title}
                  </p>
                  <p className="mt-6 font-display text-lg leading-relaxed md:text-xl">
                    &ldquo;{SAMPLE_STORY.story}&rdquo;
                  </p>
                  <p className="mt-8 text-xs font-medium uppercase tracking-[0.2em] text-cream/50">
                    Acceptance criteria
                  </p>
                  <ul className="mt-4 space-y-3">
                    {SAMPLE_STORY.criteria.map((c, i) => (
                      <li key={i} className="flex gap-3 text-sm leading-relaxed text-cream/75">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-rust" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section data-testid="doccare-cta" className="py-24 md:py-32">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
              <div className="border border-navy/15 bg-white p-10 md:p-16">
                <Eyebrow>Your Product</Eyebrow>
                <h2 className="mt-5 max-w-2xl font-display text-4xl font-semibold leading-[1.15] tracking-tight md:text-5xl">
                  Have a healthcare or booking product in mind?
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/70">
                  The same approach — requirements first, then a
                  development-ready definition — can be applied to your idea.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/#contact"
                    data-testid="doccare-cta-discuss-btn"
                    className="group inline-flex items-center gap-2 rounded-full bg-rust px-7 py-3.5 text-sm font-medium text-white transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    Discuss Your Project
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                  <Link
                    to="/#services"
                    data-testid="doccare-cta-services-btn"
                    className="inline-flex items-center rounded-full border border-navy/25 px-7 py-3.5 text-sm font-medium transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-cream"
                  >
                    Explore Services
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
